import { lazyWithExpiration, memoize, weakMemoize } from "nodash"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { S3Client } from "@aws-sdk/client-s3"
import { Upload } from "@aws-sdk/lib-storage"
import { SupportedRegions } from "@nova/constants"
import { getIngestBucket, getSampleDataBucket } from "@nova/util-s3/client"
import { config, isLocalhostEnvironment } from "@nova/configuration/browser"
import { CopyIngestPayload, IngestDataType, IngestPayload } from "@nova/schemas"
import { triggerMockIngest } from "@nova/util-ingest"

type WriteCredentials = {
    accessKeyId: string
    secretAccessKey: string
    sessionToken: string
    expiration: string
}

type CloudfrontBuckets = "data" | "agora" | "weather"

export type FileManagementProps = {
    userId: string
    orgId: string
    projectId?: string
}

type PutFileOptions = (
    | {
          target: string
          dataType?: IngestDataType.Copy | IngestDataType.ProfilePhoto
          regionOverride?: SupportedRegions
      }
    | { filename: string; tags: IngestPayload }
) & {
    onProgress?: (loaded: number, total: number) => void
    onS3Complete?: () => void
}

const buildCloudfrontDataUrl = (
    s3Path: string,
    region: SupportedRegions,
    bucket: CloudfrontBuckets = "data"
) => {
    const regionPrefix = region.split("-")[0]
    if (isLocalhostEnvironment())
        return `http://localhost:3000/cdn/${regionPrefix}/${bucket}/${s3Path}`
    const domain = config.get("cloudfrontDomain")
    return `https://${domain}/${regionPrefix}/${bucket}/${s3Path}`
}

export class FileManagement {
    private data:
        | { initialized: false }
        | {
              initialized: true
              userId: string
              orgId: string
              projectId?: string
              getCredentials: () => Promise<WriteCredentials | undefined>
          }

    private region = SupportedRegions.Canada
    constructor() {
        this.data = { initialized: false }
    }

    private getS3Client = memoize((region: SupportedRegions) =>
        weakMemoize(
            (credentials: WriteCredentials) =>
                new S3Client({
                    credentials: { ...credentials, expiration: new Date(credentials.expiration) },
                    region,
                })
        )
    )

    initialize({
        userId,
        orgId,
        projectId,
        region,
    }: FileManagementProps & { region: SupportedRegions }) {
        const getCredentials = lazyWithExpiration(
            async () => {
                if (projectId) {
                    const creds = await fetchApiGateway(
                        "post /org/:orgId/project/:projectId/s3-auth",
                        { params: { orgId, projectId } }
                    )
                    return creds.credentials
                } else {
                    const creds = await fetchApiGateway("post /org/:orgId/s3-auth", {
                        params: { orgId },
                    })
                    return creds.credentials
                }
            },
            59 * 60 * 1000
        )
        this.data = { initialized: true, orgId, projectId, getCredentials, userId }
        this.region = region
    }

    cleanup() {
        this.data = { initialized: false }
    }

    async getDownloadUrl(
        path: string,
        {
            bucket = "data",
            isDemo = false,
            region = this.region,
        }: { bucket?: CloudfrontBuckets; isDemo?: boolean; region?: SupportedRegions } = {}
    ) {
        if (isDemo) {
            const bucketName = getSampleDataBucket(region)
            return `https://${bucketName}.s3.${region}.amazonaws.com/${path}`
        }
        if (!this.data.initialized)
            throw new Error("Trying to get file url when credentials aren't initialized")
        await this.data.getCredentials()
        return buildCloudfrontDataUrl(path, region, bucket)
    }

    getPublicAssetUrl(
        path: string,
        {
            bucket = "data",
            region = this.region,
        }: { bucket?: CloudfrontBuckets; region?: SupportedRegions } = {}
    ) {
        return buildCloudfrontDataUrl(path, region, bucket)
    }

    // handles multipart uploads automatically
    async putFile(body: Blob | Uint8Array, options: PutFileOptions) {
        if (!this.data.initialized)
            throw new Error("Trying to get file url when credentials aren't initialized")

        const { tags, filename, region } = (() => {
            if ("target" in options) {
                while (options.target.startsWith("/")) {
                    options.target = options.target.slice(1)
                }
                const tags = {
                    dataType: options.dataType ?? IngestDataType.Copy,
                    nextPath: options.target,
                    orgId: this.data.orgId,
                } as CopyIngestPayload
                if (this.data.projectId) tags.projectId = this.data.projectId
                return {
                    tags,
                    filename: options.target.split("/").at(-1),
                    region: options.regionOverride || this.region,
                }
            } else {
                return { tags: options.tags, filename: options.filename, region: this.region }
            }
        })()

        const credentials = await this.data.getCredentials()
        if (!credentials) throw new Error("Write credentials not configured")
        const s3 = this.getS3Client(region)(credentials)

        const stringifiedTags = Object.fromEntries(
            Object.entries(tags).map(([key, value]) => [key, value.toString()])
        )

        const tagging = new URLSearchParams(stringifiedTags).toString()

        const upload = new Upload({
            client: s3,
            params: {
                Body: body,
                Key: `user/${this.data.userId}/${filename}`,
                Bucket: getIngestBucket(region),
                Tagging: tagging,
            },
        })
        if (options.onProgress) {
            const size = body instanceof Blob ? body.size : body.length
            upload.on("httpUploadProgress", (progress) => {
                options.onProgress!(progress.loaded ?? 0, progress.total ?? size)
            })
        }
        const result = await upload.done()
        options.onS3Complete?.()
        const ETag = result.ETag?.replace(/^"|"$/g, "")
        if (filename && isLocalhostEnvironment() && this.data.initialized) {
            triggerMockIngest({
                url: "/api-gateway/mock-ingest-server",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: {
                    userId: this.data.userId,
                    s3Key: `user/${this.data.userId}/${filename}`,
                    payload: tags,
                    region: region,
                    filename: filename,
                    requestId: ETag!,
                    size: "size" in body ? body.size : body.length,
                },
            }).catch(console.error)
        }
        return { ETag }
    }

    async fetchFile(
        path: string,
        {
            bucket = "data",
            isDemo = false,
            signal,
            region,
        }: {
            bucket?: CloudfrontBuckets
            isDemo?: boolean
            signal?: AbortSignal
            region?: SupportedRegions
        } = {}
    ) {
        const url = await this.getDownloadUrl(path, { bucket, isDemo, region })
        return fetch(url, { credentials: isDemo ? "omit" : "include", signal })
    }
}
